import {
  Autocomplete,
  Avatar,
  Checkbox,
  FilterOptionsState,
  IconButton,
  ListItem,
  Paper,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import {
  AccordionArrowPurple,
  Checkmark,
  CheckmarkChecked,
  PurpleClose,
  PurpleX,
} from 'Images';
import { useEffect, useRef, useState } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import { infiniteScrollLoaderHandler } from 'Utils';

const halfResultScrollJumpSize = DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT / 2;

interface ILocations extends UseControllerProps {
  options: {
    id: number;
    value: string;
  }[];
  placeholder?: string;
}

const Locations = ({
  options,
  placeholder,
  ...controllerProps
}: ILocations) => {
  const [scrollIndex, setScrollIndex] = useState<number>(0);
  const {
    field: { onChange, value, ref, ...field },
    fieldState: { error },
  } = useController(controllerProps);

  const handleChange = (__: any, newValue: string[]) => {
    onChange(
      newValue.map(
        (option: string) => options.find((opt) => opt.value === option)?.id
      )
    );
  };

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Suppliers
  ).doctorsSearchForm;

  const getValue = () => options.filter((option) => value.includes(option.id));

  const listRef = useRef<any>(null);

  useEffect(() => {
    if (listRef.current) {
      const list = listRef.current.children[0];
      list.scrollTo({ top: list.scrollHeight / 2 - 100 });
    }
  }, [scrollIndex]);

  return (
    <Stack gap='20px'>
      <Typography variant='body2'>{dictionary.location}</Typography>
      <Autocomplete
        ChipProps={{
          sx: {
            bgcolor: '#A020FF',
            color: 'white',
          },
          deleteIcon: (
            <IconButton>
              <Avatar
                alt={Dictionary[DICTIONARY.General].close}
                src={PurpleClose}
                sx={{ width: '20px', height: '20px' }}
              />
            </IconButton>
          ),
          role: 'none',
        }}
        noOptionsText={Dictionary[DICTIONARY.General].noOptions}
        filterOptions={(
          options: string[],
          state: FilterOptionsState<string>
        ) => {
          const inputValue = state.inputValue;
          if (!options.some((option) => option.includes(inputValue))) {
            state.inputValue = '';
          }

          let MaxViewList: string[] = [];
          const filtered = createFilterOptions<string>({})(options, state);

          MaxViewList = [...filtered];
          if (state.inputValue.length > 0) {
            MaxViewList = MaxViewList.slice(
              0,
              DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT
            );
          } else {
            if (
              scrollIndex * DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT >
              options.length - DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT
            )
              MaxViewList = MaxViewList.slice(
                scrollIndex * DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT -
                  DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT,
                options.length - 1
              );
            else
              MaxViewList = MaxViewList.slice(
                scrollIndex * halfResultScrollJumpSize,
                (scrollIndex + 1) * halfResultScrollJumpSize +
                  halfResultScrollJumpSize
              );
          }

          return MaxViewList;
        }}
        sx={{
          '& fieldset': {
            borderColor: 'primary.shade_200',
            borderRadius: '6px',
            mt: '5px',
          },
          '.MuiOutlinedInput-root': {
            borderRadius: '6px',
          },
          '&&& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '6px',
            borderColor: 'primary.shade_200',
            borderWidth: '1px',
          },
          '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
            borderColor: 'primary.main',
            borderRadius: '6px',
            borderWidth: '1px',
          },
          '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
            border: '1px solid',
            borderColor: 'primary.shade_200',
          },
        }}
        popupIcon={
          <Avatar
            alt=''
            src={AccordionArrowPurple}
            sx={{
              width: '32px',
              height: '32px',
              mt: '-4px',
            }}
          />
        }
        clearIcon={
          <Avatar
            alt=''
            src={PurpleX}
            sx={{
              width: '20px',
              height: '20px',
            }}
          />
        }
        multiple
        options={options.map((option) => option.value)}
        disableCloseOnSelect
        getOptionLabel={(option) => option}
        renderOption={(props, option, { selected }) => (
          <ListItem {...props}>
            <Checkbox
              icon={
                <Avatar
                  alt=''
                  src={Checkmark}
                  sx={{
                    width: '24px',
                    height: '24px',
                  }}
                />
              }
              checkedIcon={
                <Avatar
                  alt=''
                  src={CheckmarkChecked}
                  sx={{
                    width: '24px',
                    height: '24px',
                  }}
                />
              }
              checked={selected}
            />
            <Typography variant='body3'> {option}</Typography>
          </ListItem>
        )}
        renderInput={(params) => (
          <>
            <span className='sr-only'>{placeholder}</span>
            <TextField
              {...params}
              placeholder={getValue().length === 0 ? placeholder : ''}
              inputRef={ref}
              error={Boolean(error)}
              helperText={error ? error.message : ''}
              inputProps={{
                ...params.inputProps,
                'aria-label': dictionary.location,
              }}
            />
          </>
        )}
        value={getValue().map((option) => option.value)}
        onChange={handleChange}
        {...field}
        PaperComponent={(props) => (
          <Paper
            {...props}
            sx={{
              maxHeight: '300px',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0px 4px 24px rgba(45, 112, 217, 0.16)',
              color: 'text.secondary',
              fontWeight: 400,

              '&&& .Mui-focused': {
                fontWeight: 700,
                bgcolor: 'primary.shade_100',
              },

              '& .MuiAutocomplete-listbox': {
                maxHeight: '300px',
                p: 0,
              },
            }}
            onScrollCapture={(event) =>
              infiniteScrollLoaderHandler({
                event,
                scrollIndex,
                setScrollIndex,
                optionsLength: options.length,
              })
            }
            ref={listRef}
          />
        )}
      />
    </Stack>
  );
};

export default Locations;
